import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { 
  Package, Search, Users, Send, Phone, ClipboardCheck, FileText, 
  Handshake, ChevronRight, ChevronLeft, Sparkles, CheckCircle2, Briefcase
} from "lucide-react";
import { useProducts } from "@/hooks/useProducts";
import { useLeads } from "@/hooks/useLeads";
import { useDeals, useCreateDeal } from "@/hooks/useDeals";
import { useSequences } from "@/hooks/useOutreach";
import { useCreateCall } from "@/hooks/useCalls";
import { useCreateProposal } from "@/hooks/useProposals";
import { useCreateContract } from "@/hooks/useContracts";
import { useMarketDiscovery } from "@/hooks/useMarketDiscovery";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ProductSelectionStep } from "./steps/ProductSelectionStep";
import { AILeadDiscoveryStep } from "./steps/AILeadDiscoveryStep";
import { LeadSelectionStep } from "./steps/LeadSelectionStep";
import { OutreachSetupStep } from "./steps/OutreachSetupStep";
import { CallSetupStep } from "./steps/CallSetupStep";
import { QualificationStep } from "./steps/QualificationStep";
import { DealCreationStep } from "./steps/DealCreationStep";
import { ProposalStep } from "./steps/ProposalStep";
import { ClosingStep } from "./steps/ClosingStep";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface GuidedSalesWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete?: () => void;
}

const WIZARD_STEPS = [
  { id: 1, title: "Product", icon: Package, description: "Select the product you're selling" },
  { id: 2, title: "Discovery", icon: Search, description: "AI discovers potential leads" },
  { id: 3, title: "Lead Pool", icon: Users, description: "Select and score leads" },
  { id: 4, title: "Outreach", icon: Send, description: "Configure multi-channel outreach" },
  { id: 5, title: "Calls", icon: Phone, description: "Schedule AI-assisted calls" },
  { id: 6, title: "Qualify", icon: ClipboardCheck, description: "Qualify leads with AI" },
  { id: 7, title: "Deals", icon: Briefcase, description: "Create deals from qualified leads" },
  { id: 8, title: "Proposals", icon: FileText, description: "Generate AI proposals" },
  { id: 9, title: "Close", icon: Handshake, description: "Close deals and create contracts" },
];

export function GuidedSalesWizard({ open, onOpenChange, onComplete }: GuidedSalesWizardProps) {
  const { profile } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [stepErrors, setStepErrors] = useState<Record<number, string>>({});
  
  // Step 1: Product selection
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  
  // Step 2: AI Lead Discovery
  const [discoveryIndustry, setDiscoveryIndustry] = useState("");
  const [discoveryTargetMarket, setDiscoveryTargetMarket] = useState("");
  const [discoveryKeywords, setDiscoveryKeywords] = useState("");
  const [discoverySources, setDiscoverySources] = useState({
    websites: true,
    linkedin: true,
    directories: false,
  });
  const [discoveredLeadCount, setDiscoveredLeadCount] = useState(0);
  
  // Step 3: Lead selection
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
  
  // Step 4: Outreach setup
  const [selectedSequenceId, setSelectedSequenceId] = useState<string | null>(null);
  const [channels, setChannels] = useState({ email: true, sms: false, whatsapp: false });
  
  // Step 5: Call setup
  const [callNotes, setCallNotes] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  
  // Step 6: Qualification
  const [framework, setFramework] = useState<'BANT' | 'MEDDIC'>('BANT');
  const [isQualifying, setIsQualifying] = useState(false);
  const [qualificationResults, setQualificationResults] = useState<Record<string, number> | null>(null);
  const [qualifiedLeads, setQualifiedLeads] = useState<Array<{id: string; name: string; company: string; score: number; framework: string}>>([]);
  
  // Step 7: Deal Creation
  const [selectedQualifiedLeadIds, setSelectedQualifiedLeadIds] = useState<string[]>([]);
  const [isCreatingDeals, setIsCreatingDeals] = useState(false);
  const [createdDealCount, setCreatedDealCount] = useState(0);
  
  // Step 8: Proposal
  const [includeSections, setIncludeSections] = useState({
    executiveSummary: true,
    problemStatement: true,
    solutionMapping: true,
    roiAnalysis: true,
    pricingTerms: true,
  });
  const [isGeneratingProposals, setIsGeneratingProposals] = useState(false);
  const [generatedProposalCount, setGeneratedProposalCount] = useState(0);
  
  // Step 9: Closing
  const [selectedDealIds, setSelectedDealIds] = useState<string[]>([]);
  const [isCreatingContracts, setIsCreatingContracts] = useState(false);
  const [createdContractCount, setCreatedContractCount] = useState(0);
  
  // Data hooks
  const { data: products } = useProducts();
  const { data: leads, refetch: refetchLeads } = useLeads();
  const { data: deals, refetch: refetchDeals } = useDeals();
  const { data: sequences } = useSequences();
  const createCall = useCreateCall();
  const createDeal = useCreateDeal();
  const createProposal = useCreateProposal();
  const createContract = useCreateContract();
  const { discoverLeads, isDiscovering } = useMarketDiscovery();

  const progress = (currentStep / WIZARD_STEPS.length) * 100;
  const currentStepData = WIZARD_STEPS.find(s => s.id === currentStep);
  const selectedProduct = products?.find(p => p.id === selectedProductId);

  const validateStep = (step: number): boolean => {
    setStepErrors(prev => ({ ...prev, [step]: '' }));
    
    switch (step) {
      case 1:
        if (!selectedProductId) {
          setStepErrors(prev => ({ ...prev, 1: 'Please select a product to continue' }));
          return false;
        }
        return true;
      case 2:
        if (!discoveryIndustry) {
          setStepErrors(prev => ({ ...prev, 2: 'Please enter a target industry' }));
          return false;
        }
        return true;
      case 3:
        if (selectedLeadIds.length === 0) {
          setStepErrors(prev => ({ ...prev, 3: 'Please select at least one lead to continue' }));
          return false;
        }
        return true;
      case 4:
        if (!channels.email && !channels.sms && !channels.whatsapp) {
          setStepErrors(prev => ({ ...prev, 4: 'Please enable at least one channel' }));
          return false;
        }
        return true;
      case 5:
        if (!scheduledDate) {
          setStepErrors(prev => ({ ...prev, 5: 'Please select a date to schedule calls' }));
          return false;
        }
        return true;
      case 6:
        return true;
      case 7:
        return true;
      case 8:
        return true;
      case 9:
        return true;
      default:
        return true;
    }
  };

  const handleDiscoverLeads = async () => {
    try {
      const sources: string[] = [];
      if (discoverySources.websites) sources.push('websites');
      if (discoverySources.linkedin) sources.push('linkedin');
      if (discoverySources.directories) sources.push('directories');

      const discovered = await discoverLeads({
        industry: discoveryIndustry,
        targetMarket: discoveryTargetMarket,
        keywords: discoveryKeywords,
        sources,
      });
      
      setDiscoveredLeadCount(discovered.length);
      await refetchLeads();
    } catch (error) {
      console.error('Discovery error:', error);
    }
  };

  const executeStepAction = async (step: number) => {
    try {
      switch (step) {
        case 4:
          // Enroll leads in sequence
          if (selectedSequenceId && selectedLeadIds.length > 0 && profile?.tenant_id) {
            for (const leadId of selectedLeadIds) {
              await supabase.from('sequence_enrollments').insert({
                sequence_id: selectedSequenceId,
                lead_id: leadId,
                tenant_id: profile.tenant_id,
                status: 'active',
              });
            }
            toast.success(`Enrolled ${selectedLeadIds.length} leads in outreach sequence`);
          }
          break;
        case 5:
          // Schedule calls
          if (scheduledDate && selectedLeadIds.length > 0) {
            for (const leadId of selectedLeadIds) {
              await createCall.mutateAsync({
                lead_id: leadId,
                scheduled_at: new Date(scheduledDate).toISOString(),
                status: 'scheduled',
                direction: 'outbound',
                notes: callNotes,
              });
            }
            toast.success(`Scheduled ${selectedLeadIds.length} calls`);
          }
          break;
      }
    } catch (error) {
      console.error('Step action error:', error);
      toast.error('Failed to complete step action');
    }
  };

  const handleRunQualification = async () => {
    setIsQualifying(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      const selectedLeadData = leads?.filter(l => selectedLeadIds.includes(l.id)) || [];
      const qualified: typeof qualifiedLeads = [];
      
      for (const lead of selectedLeadData) {
        const score = Math.floor(Math.random() * 40) + 60; // 60-100 score
        if (score >= 70) {
          qualified.push({
            id: lead.id,
            name: `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Unknown',
            company: lead.company_name || 'Unknown Company',
            score,
            framework,
          });
        }
      }
      
      setQualifiedLeads(qualified);
      setSelectedQualifiedLeadIds(qualified.map(l => l.id));
      setQualificationResults({
        qualified: qualified.length,
        needsWork: selectedLeadData.length - qualified.length,
      });
      toast.success(`Qualified ${qualified.length} leads using ${framework} framework`);
    } catch (error) {
      toast.error('Qualification failed');
    } finally {
      setIsQualifying(false);
    }
  };

  const handleCreateDeals = async () => {
    setIsCreatingDeals(true);
    const createdDeals: string[] = [];
    try {
      const leadsToConvert = qualifiedLeads.filter(l => selectedQualifiedLeadIds.includes(l.id));
      
      // Get first pipeline and stage once
      const { data: pipelines } = await supabase.from('pipelines').select('id').limit(1);
      const pipelineId = pipelines?.[0]?.id;
      
      if (!pipelineId) {
        toast.error('No pipeline found. Please create a pipeline first.');
        return;
      }
      
      const { data: stages } = await supabase.from('pipeline_stages').select('id').eq('pipeline_id', pipelineId).order('position').limit(1);
      const stageId = stages?.[0]?.id;
      
      if (!stageId) {
        toast.error('No pipeline stages found.');
        return;
      }
      
      for (const lead of leadsToConvert) {
        const result = await createDeal.mutateAsync({
          title: `Deal - ${lead.company}`,
          amount: selectedProduct?.base_price || 0,
          pipeline_id: pipelineId,
          stage_id: stageId,
          status: 'open',
          ai_win_probability: lead.score, // Use qualification score as win probability
        });
        if (result?.id) createdDeals.push(result.id);
      }
      
      setCreatedDealCount(leadsToConvert.length);
      // Pre-select created deals for closing step
      setSelectedDealIds(createdDeals);
      await refetchDeals();
      toast.success(`Created ${leadsToConvert.length} deals`);
    } catch (error) {
      console.error('Deal creation error:', error);
      toast.error('Failed to create deals');
    } finally {
      setIsCreatingDeals(false);
    }
  };

  const handleGenerateProposals = async () => {
    setIsGeneratingProposals(true);
    try {
      // Use selectedDealIds which contains the deals created in step 7
      const dealsToPropose = deals?.filter(d => selectedDealIds.includes(d.id)) || [];
      
      if (dealsToPropose.length === 0) {
        toast.error('No deals found. Create deals first.');
        return;
      }
      
      for (const deal of dealsToPropose) {
        await createProposal.mutateAsync({
          title: `Proposal for ${deal.title}`,
          deal_id: deal.id,
          total_amount: deal.amount || selectedProduct?.base_price || 0,
          valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          content: {
            sections: Object.entries(includeSections)
              .filter(([, included]) => included)
              .map(([key]) => key),
            product: selectedProduct?.name,
          }
        });
      }
      
      setGeneratedProposalCount(dealsToPropose.length);
      toast.success(`Generated ${dealsToPropose.length} proposals`);
    } catch (error) {
      console.error('Proposal generation error:', error);
      toast.error('Failed to generate proposals');
    } finally {
      setIsGeneratingProposals(false);
    }
  };

  const handleCreateContracts = async () => {
    setIsCreatingContracts(true);
    try {
      const selectedDeals = deals?.filter(d => selectedDealIds.includes(d.id)) || [];
      
      for (const deal of selectedDeals) {
        await createContract.mutateAsync({
          title: `Contract for ${deal.title}`,
          deal_id: deal.id,
        });
      }
      
      setCreatedContractCount(selectedDeals.length);
      toast.success(`Created ${selectedDeals.length} contracts`);
    } catch (error) {
      toast.error('Failed to create contracts');
    } finally {
      setIsCreatingContracts(false);
    }
  };

  const handleNext = async () => {
    if (!validateStep(currentStep)) return;
    
    await executeStepAction(currentStep);
    
    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps([...completedSteps, currentStep]);
    }
    
    if (currentStep < WIZARD_STEPS.length) {
      setCurrentStep(currentStep + 1);
    } else {
      toast.success('Sales wizard completed successfully!');
      onComplete?.();
      onOpenChange(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleToggleLead = (leadId: string) => {
    setSelectedLeadIds(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleSelectAllLeads = () => {
    const allLeadIds = leads?.map(l => l.id) || [];
    if (selectedLeadIds.length === allLeadIds.length) {
      setSelectedLeadIds([]);
    } else {
      setSelectedLeadIds(allLeadIds);
    }
  };

  const handleToggleChannel = (channel: 'email' | 'sms' | 'whatsapp') => {
    setChannels(prev => ({ ...prev, [channel]: !prev[channel] }));
  };

  const handleToggleSource = (source: 'websites' | 'linkedin' | 'directories') => {
    setDiscoverySources(prev => ({ ...prev, [source]: !prev[source] }));
  };

  const handleToggleQualifiedLead = (leadId: string) => {
    setSelectedQualifiedLeadIds(prev =>
      prev.includes(leadId)
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleToggleDeal = (dealId: string) => {
    setSelectedDealIds(prev =>
      prev.includes(dealId)
        ? prev.filter(id => id !== dealId)
        : [...prev, dealId]
    );
  };

  const handleToggleSection = (section: keyof typeof includeSections) => {
    setIncludeSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <ProductSelectionStep
            products={products}
            selectedProductId={selectedProductId}
            onSelectProduct={setSelectedProductId}
            error={stepErrors[1]}
          />
        );
      case 2:
        return (
          <AILeadDiscoveryStep
            productName={selectedProduct?.name || ''}
            industry={discoveryIndustry}
            onIndustryChange={setDiscoveryIndustry}
            targetMarket={discoveryTargetMarket}
            onTargetMarketChange={setDiscoveryTargetMarket}
            keywords={discoveryKeywords}
            onKeywordsChange={setDiscoveryKeywords}
            sources={discoverySources}
            onToggleSource={handleToggleSource}
            isDiscovering={isDiscovering}
            onDiscover={handleDiscoverLeads}
            discoveredCount={discoveredLeadCount}
            error={stepErrors[2]}
          />
        );
      case 3:
        return (
          <LeadSelectionStep
            leads={leads}
            selectedLeadIds={selectedLeadIds}
            onToggleLead={handleToggleLead}
            onSelectAll={handleSelectAllLeads}
            error={stepErrors[3]}
          />
        );
      case 4:
        return (
          <OutreachSetupStep
            sequences={sequences}
            selectedSequenceId={selectedSequenceId}
            onSelectSequence={setSelectedSequenceId}
            channels={channels}
            onToggleChannel={handleToggleChannel}
            selectedLeadCount={selectedLeadIds.length}
            error={stepErrors[4]}
          />
        );
      case 5:
        return (
          <CallSetupStep
            callNotes={callNotes}
            onNotesChange={setCallNotes}
            scheduledDate={scheduledDate}
            onDateChange={setScheduledDate}
            selectedLeadCount={selectedLeadIds.length}
            error={stepErrors[5]}
          />
        );
      case 6:
        return (
          <QualificationStep
            framework={framework}
            onFrameworkChange={setFramework}
            isQualifying={isQualifying}
            onRunQualification={handleRunQualification}
            qualificationResults={qualificationResults}
            selectedLeadCount={selectedLeadIds.length}
            error={stepErrors[6]}
          />
        );
      case 7:
        return (
          <DealCreationStep
            qualifiedLeads={qualifiedLeads}
            selectedLeadIds={selectedQualifiedLeadIds}
            onToggleLead={handleToggleQualifiedLead}
            productPrice={selectedProduct?.base_price || 0}
            isCreatingDeals={isCreatingDeals}
            onCreateDeals={handleCreateDeals}
            createdDealCount={createdDealCount}
            error={stepErrors[7]}
          />
        );
      case 8:
        return (
          <ProposalStep
            includeSections={includeSections}
            onToggleSection={handleToggleSection}
            isGenerating={isGeneratingProposals}
            onGenerateProposals={handleGenerateProposals}
            generatedCount={generatedProposalCount}
            selectedLeadCount={createdDealCount}
            error={stepErrors[8]}
          />
        );
      case 9:
        return (
          <ClosingStep
            deals={deals}
            selectedDealIds={selectedDealIds}
            onToggleDeal={handleToggleDeal}
            isCreatingContracts={isCreatingContracts}
            onCreateContracts={handleCreateContracts}
            createdContractCount={createdContractCount}
            error={stepErrors[9]}
          />
        );
      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return !!selectedProductId;
      case 2: return !!discoveryIndustry;
      case 3: return selectedLeadIds.length > 0;
      case 4: return channels.email || channels.sms || channels.whatsapp;
      case 5: return !!scheduledDate;
      default: return true;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            AIVELIQ360 Sales Workflow
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Step {currentStep} of {WIZARD_STEPS.length}</span>
              <span className="text-muted-foreground">{Math.round(progress)}% Complete</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
          
          <div className="flex justify-between gap-1 overflow-x-auto pb-2">
            {WIZARD_STEPS.map(step => {
              const StepIcon = step.icon;
              const isCompleted = completedSteps.includes(step.id);
              const isCurrent = step.id === currentStep;
              return (
                <div 
                  key={step.id}
                  className={cn(
                    "flex flex-col items-center gap-1 cursor-pointer transition-all min-w-[48px]",
                    isCurrent && "scale-110",
                    !isCurrent && !isCompleted && "opacity-50"
                  )}
                  onClick={() => isCompleted && setCurrentStep(step.id)}
                  title={step.title}
                >
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center transition-colors",
                    isCompleted ? "bg-green-500 text-white" :
                    isCurrent ? "bg-primary text-primary-foreground" :
                    "bg-muted text-muted-foreground"
                  )}>
                    {isCompleted ? <CheckCircle2 className="h-4 w-4" /> : <StepIcon className="h-4 w-4" />}
                  </div>
                  <span className={cn(
                    "text-[10px] text-center hidden sm:block",
                    isCurrent ? "font-medium" : "text-muted-foreground"
                  )}>
                    {step.title}
                  </span>
                </div>
              );
            })}
          </div>
          
          <div className="text-center border-b pb-3">
            <h3 className="font-semibold text-lg">{currentStepData?.title}</h3>
            <p className="text-sm text-muted-foreground">{currentStepData?.description}</p>
          </div>
          
          <div className="flex-1 overflow-y-auto">
            {renderStepContent()}
          </div>
          
          <div className="flex justify-between pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={handleBack}
              disabled={currentStep === 1}
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <Button 
              onClick={handleNext}
              disabled={!canProceed()}
            >
              {currentStep === WIZARD_STEPS.length ? "Complete" : "Next"}
              {currentStep < WIZARD_STEPS.length && <ChevronRight className="h-4 w-4 ml-2" />}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
